import Image from 'next/image';
import { useEffect, useRef, useState } from "react";
import { ProductGalleryItemProps, ProductVariantItemProps } from './constants';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper';
import { useAttributesStore } from '@/stores/useAttributesStore';

import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css";


interface ProductGalleryProps {
  variantId: number;
  image_gallery: Array<ProductGalleryItemProps>;
  product_code: string;
  name: string;
  product_variant_list: Array<ProductVariantItemProps>;
  product_label_right_top: string | null;
  product_label_right_product_page: string | null;
  product_label_left_product_page: string | null;
  use_product_option: boolean;
  clickAttribute: boolean;
  selectColor: number;
}

const Gallery = (props: ProductGalleryProps) => {
  const { product_code, image_gallery, name, use_product_option } = props;
  const attributesData = useAttributesStore(state => state.attributes);
  const first_image = (image_gallery && image_gallery.length ? image_gallery[0].image : '');

  const [originalName, setOriginalName] = useState('');
  // 初始一律顯示全部圖片
  const [mainImg, setMainImg] = useState(image_gallery && image_gallery.length ? image_gallery[0].image : first_image);
  const [mainImgDesc, setMainImgDesc] = useState(image_gallery && image_gallery.length ? image_gallery[0].desc : "product gallery");
  const [gallery, setGallery] = useState(image_gallery);

  const swiperRef = useRef(null);
  const swiperMobileRef = useRef(null);

  const handleClick = (image: string, desc: string) => {
    if (mainImg !== image) {
      setMainImg(image);
      setMainImgDesc(desc);
    }
  };

  useEffect(() => {
    // 只有在 clickAttribute 為 true 時才根據 attribute 過濾
    if (props.clickAttribute) {
      if (props.selectColor) {
        const filtered_gallery = image_gallery.filter((gallery) => gallery.attribute_value.find((value) => props.selectColor === value));
        if (filtered_gallery.length > 0) {
          if (JSON.stringify(gallery) !== JSON.stringify(filtered_gallery)) {
            setMainImg(filtered_gallery[0].image);
            setGallery(filtered_gallery);
            if (swiperRef && swiperRef.current) {
              (swiperRef.current as any).swiper.slideTo(0);
            }
            if (swiperMobileRef && swiperMobileRef.current) {
              (swiperMobileRef.current as any).swiper.slideTo(0);
            }
          }
        } else {
          const filtered_gallery = image_gallery.filter((gallery) => gallery.attribute_value.length === 0);
          if (filtered_gallery.length > 0) {
            if (JSON.stringify(gallery) !== JSON.stringify(filtered_gallery)) {
              setMainImg(filtered_gallery[0].image);
              setGallery(filtered_gallery);
              if (swiperRef && swiperRef.current) {
                (swiperRef.current as any).swiper.slideTo(0);
              }
              if (swiperMobileRef && swiperMobileRef.current) {
                (swiperMobileRef.current as any).swiper.slideTo(0);
              }
            }
          }
        }
      } else if (props.variantId) {
        const variant = props.product_variant_list.find((variant) => variant.variant_id === props.variantId);
        if (variant) {
          attributesData.data.map((attributes) => {
            const filtered = attributes.attribute_values.filter((attribute) => variant.attribute_value_list.some((value) => attribute.id === value)).sort((a, b) => (a.order === b.order) ? a.id - b.id : a.order - b.order);
            if (filtered.length > 0) {
              const filtered_gallery = image_gallery.filter((gallery) => gallery.attribute_value.find((value) => filtered.find((attribute) => attribute.id === value)));
              if (filtered_gallery.length > 0) {
                if (JSON.stringify(gallery) !== JSON.stringify(filtered_gallery)) {
                  setMainImg(filtered_gallery[0].image);
                  setGallery(filtered_gallery);
                  if (swiperRef && swiperRef.current) {
                    (swiperRef.current as any).swiper.slideTo(0);
                  }
                  if (swiperMobileRef && swiperMobileRef.current) {
                    (swiperMobileRef.current as any).swiper.slideTo(0);
                  }
                }
              } else {
                const filtered_gallery = image_gallery.filter((gallery) => gallery.attribute_value.length === 0);
                if (filtered_gallery.length > 0) {
                  if (JSON.stringify(gallery) !== JSON.stringify(filtered_gallery)) {
                    setMainImg(filtered_gallery[0].image);
                    setGallery(filtered_gallery);
                    if (swiperRef && swiperRef.current) {
                      (swiperRef.current as any).swiper.slideTo(0);
                    }
                    if (swiperMobileRef && swiperMobileRef.current) {
                      (swiperMobileRef.current as any).swiper.slideTo(0);
                    }
                  }
                }
              }
            }
          });
        }
      }
    } else {
      // 初始或非點選 attribute 時，顯示全部圖片
      if (JSON.stringify(gallery) !== JSON.stringify(image_gallery)) {
        setMainImg(image_gallery && image_gallery.length ? image_gallery[0].image : first_image);
        setGallery(image_gallery);
        if (swiperRef && swiperRef.current) {
          (swiperRef.current as any).swiper.slideTo(0);
        }
        if (swiperMobileRef && swiperMobileRef.current) {
          (swiperMobileRef.current as any).swiper.slideTo(0);
        }
      }
    }
  }, [props.selectColor, props.clickAttribute, props.variantId, image_gallery, attributesData.data, first_image, gallery, props.product_variant_list])

  useEffect(() => {
    if (originalName != product_code) {
      setMainImg(image_gallery && image_gallery.length ? image_gallery[0].image : first_image);
      setMainImgDesc(image_gallery && image_gallery.length ? image_gallery[0].desc : "product gallery");
      setOriginalName(product_code)

      // force move to first slide
      if (swiperRef && swiperRef.current) {
        (swiperRef.current as any).swiper.slideTo(0);
      }

      if (swiperMobileRef && swiperMobileRef.current) {
        (swiperMobileRef.current as any).swiper.slideTo(0);
      }
    }
  }, [image_gallery, first_image, originalName, mainImg, product_code])

  return (
    <section className="col-span-1 md:col-span-4" aria-label="product gallery">
      <div className="md:hidden">
        <Swiper
          slidesPerView={1}
          modules={[Pagination, Navigation]}
          speed={700}
          navigation={false}
          pagination={true}
          className="product-gallery mySwiper !pb-5"
          ref={swiperMobileRef}
        >
          {gallery.map((c, index) => (
            <SwiperSlide key={index} className="relative flex justify-center">
              <Image
                src={c.image}
                width={800}
                height={800}
                alt={c.desc ? c.desc : "product gallery"}
                quality={100}
                className={`w-full cursor-pointer !object-contain aspect-square`}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <div className="hidden md:block md:sticky h-fit top-[120px]">
        <div className="relative mb-2">
          <Image
            src={mainImg}
            width={800}
            height={800}
            alt={mainImgDesc ? mainImgDesc : "product image"}
            quality={100}
            className={`w-full !object-contain aspect-square`}
          />
        </div>
        <div className="relative">
          <Swiper
            slidesPerView={4.8}
            spaceBetween={10}
            modules={[Pagination, Navigation]}
            speed={700}
            navigation={true}
            pagination={false}
            className="mySwiper"
            ref={swiperRef}
          >
            {gallery.map((c, index) => (
              <SwiperSlide key={index}>
                <Image
                  src={c.image}
                  width={100}
                  height={100}
                  alt={c.desc ? c.desc : "product image"}
                  quality={100}
                  className={`w-full ${mainImg === c.image ? 'border border-mooimom' : ''} cursor-pointer aspect-square !object-contain`}
                  onClick={() => handleClick(c.image, c.desc ? c.desc : "product gallery")}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  )
}

export default Gallery
